/**
 * Template Merge Utility
 * Merges template fights into event instances
 */

import { loadTemplate } from '../services/firestore';
import type { Event } from '../types';

/**
 * Load an event and merge in template fights if it's an event instance
 * @param event The event to enhance with template data
 * @returns Event with template fights merged in
 */
export async function mergeTemplateIntoEvent(event: Event): Promise<Event> {
  // If this is a template itself, return as-is
  if (event.isTemplate) {
    return event;
  }
  
  // If this is an event instance with a templateId, load and merge template fights
  if (event.templateId) {
    try {
      const template = await loadTemplate(event.templateId);
      
      if (template && template.fights && template.fights.length > 0) {
        // Merge template fights with any local-only changes (like 'in-progress' status)
        const mergedFights = template.fights.map(templateFight => {
          const localFight = event.fights?.find(f => f.id === templateFight.id);
          
          // Preserve 'in-progress' only if template fight has no result yet
          if (localFight?.status === 'in-progress' && !templateFight.result) {
            return { ...templateFight, status: 'in-progress' as const };
          }
          return templateFight;
        });
        
        return {
          ...event,
          fights: mergedFights,
          sport: template.sport || event.sport,
          status: template.status || event.status,
          finished: template.finished || event.finished,
          finishedAt: template.finishedAt || event.finishedAt,
        };
      }
    } catch (err) {
      console.error(`[mergeTemplateIntoEvent] Error:`, err);
    }
  }
  
  // Fallback: return event as-is
  return event;
}

/**
 * Get fights for template creation/selection view (hide completed if setting enabled)
 * @param template The template
 * @returns Fights to show (may hide completed ones)
 */
export function getVisibleFightsForTemplate(template: Event): typeof template.fights {
  if (!template.isTemplate || !template.hideCompletedFights) {
    return template.fights;
  }
  
  // Hide completed fights
  return template.fights.filter(f => f.status !== 'completed');
}

/**
 * Check if a user can manage fights (add/edit/delete) for this event
 * Only ultra-admins can manage fights on templates
 * Regular event admins CANNOT manage fights
 */
export function canManageFights(event: Event, isUltraAdmin: boolean): boolean {
  // Only ultra-admins can manage fights, and only on templates
  return isUltraAdmin && event.isTemplate === true;
}

/**
 * Check if a user can enter results for fights
 * Only ultra-admins can enter results on templates
 */
export function canEnterResults(event: Event, isUltraAdmin: boolean): boolean {
  // Only ultra-admins can enter results on templates
  return isUltraAdmin && event.isTemplate === true;
}

/**
 * Check if a user can manage participants for this event
 * Event admins can manage participants on their events
 * Ultra-admins cannot manage participants on templates (templates don't have participants)
 */
export function canManageParticipants(event: Event): boolean {
  // Only event instances have participants
  return !event.isTemplate;
}

